import i18n from 'i18next';
import { QueryParamKeys } from '../../constants/query-params';
import { getQueryParam } from '../params/params';

export const I18N_LANGUAGE_STORAGE_KEY = 'i18nextLng';

export type SupportedLanguage = 'en' | 'es';

const isSupported = (value: string | null): value is SupportedLanguage => value === 'en' || value === 'es';

/**
 * localStorage, guarded.
 *
 * In modal mode the app runs inside the merchant's page, where browsers partition
 * this storage or block it outright: reads come back empty and the access itself can
 * throw. Neither is allowed to take the flow down.
 */
const readStoredLanguage = (): string | null => {
  try {
    return localStorage.getItem(I18N_LANGUAGE_STORAGE_KEY);
  } catch (err) {
    console.warn('Failed to read language preference', err);
    return null;
  }
};

const writeStoredLanguage = (language: SupportedLanguage): void => {
  try {
    localStorage.setItem(I18N_LANGUAGE_STORAGE_KEY, language);
  } catch (err) {
    console.warn('Failed to persist language preference', err);
  }
};

/**
 * Reads a previously persisted language preference, if any. A value here means
 * the user (via the toggle) or a prior auto-detect explicitly settled on a
 * language, so it should win over re-detecting from the browser.
 */
export const getStoredLanguage = (): SupportedLanguage | null => {
  const stored = readStoredLanguage();
  return isSupported(stored) ? stored : null;
};

/**
 * Narrows any language tag to one we support. i18next resolves to `undefined` before init and
 * can resolve to a tag we do not offer, so anything unrecognized reads as English.
 */
export const toSupportedLanguage = (value: string | null | undefined): SupportedLanguage => {
  const candidate = value ?? null;
  return isSupported(candidate) ? candidate : 'en';
};

/**
 * The language the app is currently displaying, read straight off the i18next singleton.
 * Components should prefer `toSupportedLanguage(i18n.resolvedLanguage)` with the `i18n` from
 * `useTranslation()`, so their re-render on `languageChanged` is visible at the call site.
 */
export const getCurrentLanguage = (): SupportedLanguage => toSupportedLanguage(i18n.resolvedLanguage ?? i18n.language);

/**
 * The language we can attribute to the customer, or null while the app is only
 * showing the English fallback it displays before anything is known.
 *
 * The distinction does not matter on screen, where English is the only sensible
 * default. It matters off it: LAS stamps dbo.Applications.ApplicationLanguage from
 * the X-Language header and tags activity logs with it, so a header sent while we
 * are merely defaulting records "the customer chose English" and cannot afterwards
 * be told apart from a real choice. That is what put en-US on the first touchpoint
 * of Spanish customers whose browser is configured in English (OR-2886).
 *
 * Held here rather than derived from localStorage because applyInitialLanguage
 * deliberately persists only Spanish, so a `language=en` launch instruction settles
 * the language without leaving anything stored to read back.
 *
 * The language itself rather than a flag paired with a live i18next read. The
 * writers below settle the language and then leave i18n.changeLanguage's promise in
 * flight, so a live read is only correct while i18next applies the change before it
 * resolves. It does today, but only because the locales are bundled at build time and
 * there is no backend to load: add one, or lazy-load a locale, and a flag would go
 * true while i18next still reported the language being replaced, which is exactly the
 * defaulted stamp this is here to prevent. Storing what we resolved does not depend on
 * that timing. Safe to capture because the writers in this file are the only callers
 * of changeLanguage in the app, so this cannot drift from what is on screen.
 */
let attributableLanguage: SupportedLanguage | null = null;

/**
 * The customer's language, or null while the app is only defaulting to English.
 *
 * Callers reporting the language to another service should send nothing on null
 * rather than substituting a default, so "we have not been told" stays a distinct
 * state downstream instead of being recorded as English.
 *
 * Toggling mid-session is reflected on subsequent reads: setCustomerLanguage
 * updates this before it hands back the pending changeLanguage.
 */
export const getCustomerLanguage = (): SupportedLanguage | null => attributableLanguage;

/**
 * Applies a language the customer chose themselves and persists it, so it outranks
 * browser detection on later visits.
 *
 * This is the language toggle's entry point. Going through here rather than writing
 * localStorage and calling i18next directly is what marks the language attributable,
 * so anything that changes the language on the customer's behalf belongs here too.
 */
export const setCustomerLanguage = (language: SupportedLanguage): Promise<unknown> => {
  writeStoredLanguage(language);
  attributableLanguage = language;
  return i18n.changeLanguage(language);
};

/**
 * Reads the `language` query param passed at launch (e.g. from P+ or another
 * originating link), if it names a language we support. This is an explicit,
 * per-launch instruction to display the app in a given language.
 */
export const getQueryLanguage = (): SupportedLanguage | null => {
  const param = getQueryParam(QueryParamKeys.Language);
  return isSupported(param) ? param : null;
};

/**
 * Removes the `language` param from the URL after it has been consumed at init.
 *
 * The param is a one-time launch instruction, not a durable preference: leaving
 * it in place would make it re-win on every reload (existing URL rewrites such
 * as setAppStatusParams rebuild from window.location.href and would carry it
 * along), permanently outranking whatever the customer later picks with the
 * toggle. Stripping it — mirroring how `entrypoint` is consumed in
 * resolveAndPersistMetadataParams — lets the param win on arrival while a later
 * reload falls back to the customer's own stored choice.
 */
const stripQueryLanguage = (): void => {
  if (!new URLSearchParams(document.location.search.substring(1)).has(QueryParamKeys.Language)) return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(QueryParamKeys.Language);
    window.history.replaceState({}, '', url.toString());
  } catch (err) {
    console.warn('Failed to strip language from URL', err);
  }
};

/** A browser language tag (e.g. 'es', 'es-MX', 'en-US') indicates Spanish. */
const prefersSpanish = (languages: readonly string[]): boolean => languages.some((lang) => lang?.toLowerCase().startsWith('es'));

const getBrowserLanguages = (): readonly string[] => (navigator.languages?.length ? navigator.languages : ([navigator.language].filter(Boolean) as string[]));

/**
 * Resolves which language the app should display.
 *
 * Precedence:
 *  1. If Spanish is not enabled for this dealer, always English — Spanish is
 *     not offered, so we never auto-detect or honor a stale Spanish preference
 *     or a `language=es` query param.
 *  2. Honor an explicit `language` query param from the launch URL. This is a
 *     deliberate per-launch instruction from the originating link, so it wins
 *     over a stored preference — a returning customer opening a link that names
 *     a language sees that language for this app.
 *  3. Honor an explicitly stored preference (user toggle or prior auto-detect).
 *  4. Auto-detect Spanish from the browser's language settings.
 *  5. Otherwise English.
 */
export const resolveInitialLanguage = ({
  queryLanguage,
  storedLanguage,
  spanishEnabled,
  browserLanguages,
}: {
  queryLanguage: SupportedLanguage | null;
  storedLanguage: SupportedLanguage | null;
  spanishEnabled: boolean;
  browserLanguages: readonly string[];
}): SupportedLanguage => {
  if (!spanishEnabled) return 'en';
  if (queryLanguage) return queryLanguage;
  if (storedLanguage) return storedLanguage;
  if (prefersSpanish(browserLanguages)) return 'es';
  return 'en';
};

/**
 * Determines the initial app language from the LD flag, any stored preference,
 * and the browser settings, then applies it via i18next.
 *
 * We only persist when the result is Spanish: an explicit toggle already writes
 * its own value, and leaving the English default unpersisted means a later
 * visit can still auto-detect Spanish once the dealer enables the flag.
 *
 * The `language` query param is consumed here and then stripped from the URL, so
 * it wins on arrival but does not survive reloads to outrank the customer's own
 * toggle choice.
 *
 * Called during app init (ecomm-machine Phase 4) after the LD client is ready
 * but before any content renders, so there is no flash of the wrong language.
 */
export const applyInitialLanguage = (spanishEnabled: boolean): SupportedLanguage => {
  const queryLanguage = getQueryLanguage();
  const storedLanguage = getStoredLanguage();
  const browserLanguages = getBrowserLanguages();

  const language = resolveInitialLanguage({ queryLanguage, storedLanguage, spanishEnabled, browserLanguages });

  // Which rule produced the answer decides whether we can attribute it to the
  // customer. A launch param, a stored preference and a positive Spanish detection
  // are each a signal about this particular customer, and Spanish being unavailable
  // settles it as well, since English is then the only language the app has.
  //
  // The trailing English default is not a signal. It fires precisely when nothing
  // has told us anything, and a browser that merely is not configured in Spanish
  // says nothing about a customer who is about to reach for the toggle.
  const attributable = !spanishEnabled || queryLanguage !== null || storedLanguage !== null || prefersSpanish(browserLanguages);
  attributableLanguage = attributable ? language : null;

  stripQueryLanguage();

  if (language === 'es') {
    writeStoredLanguage(language);
  }

  // this is just a workaround for the tests, it's a huge pain to properly set resolvedLanguage in tests
  if ((i18n.resolvedLanguage ?? i18n.language) !== language) {
    i18n.changeLanguage(language);
  }

  return language;
};

/**
 * Applies a language on the init error pages (InitError, ExpiredLink) reached
 * before applyInitialLanguage had a chance to run, so a `language` launch param
 * still takes effect when init fails early.
 *
 * Precedence: the `language` query param, then a stored preference, otherwise English.
 *
 * Deliberately looser and more conservative than applyInitialLanguage:
 *  - No Spanish flag gate: the order has not loaded, so there is no dealer to
 *    evaluate FLAG_SPANISH_ENABLED against. Only explicit signals are honored.
 *  - No browser detection: without the flag we cannot know Spanish is offered,
 *    so we do not guess it from navigator.languages.
 *  - No persistence: an error page must not set a durable preference that would
 *    outrank browser detection or the flag on the next visit.
 *
 * Attribution and URL stripping follow applyInitialLanguage: the language is only
 * attributable when the param or a stored preference settled it, and the param is
 * consumed so it does not survive a reload.
 */
export const applyFallbackLanguage = (): SupportedLanguage => {
  const queryLanguage = getQueryLanguage();
  const storedLanguage = getStoredLanguage();
  const language = queryLanguage ?? storedLanguage ?? 'en';

  attributableLanguage = queryLanguage !== null || storedLanguage !== null ? language : null;

  stripQueryLanguage();

  if ((i18n.resolvedLanguage ?? i18n.language) !== language) {
    i18n.changeLanguage(language);
  }

  return language;
};
